import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { chatAboutMeeting, fetchMeetingChatHistory } from '../services/meetings';
import { markdownToHtml } from '../utils/markdownRenderers';

const normalizeSegmentIds = (value) => {
  if (!value) {
    return [];
  }
  let rawValue = value;
  if (!Array.isArray(rawValue)) {
    try {
      const parsed = JSON.parse(rawValue);
      rawValue = Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return rawValue
    .map((entry) => {
      const numberValue = Number(entry);
      return Number.isFinite(numberValue) ? numberValue : null;
    })
    .filter((entry) => entry !== null);
};

// Helper to find the latest assistant message in a chronological array
const getLastAssistantSegmentsFromChronological = (history) => {
  for (let i = history.length - 1; i >= 0; i -= 1) {
    const message = history[i];
    if (message.role === 'assistant') {
      return Array.isArray(message.relevant_segment_ids) ? message.relevant_segment_ids : [];
    }
  }
  return [];
};

const ChatPanel = ({ sessionId, onHighlight, disabled = false }) => {
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const messageIdRef = useRef(0);
  const onHighlightRef = useRef(onHighlight);

  useEffect(() => {
    onHighlightRef.current = onHighlight;
  }, [onHighlight]);

  useEffect(() => {
    if (!sessionId) {
      setMessages([]);
      if (onHighlightRef.current) {
        onHighlightRef.current([]);
      }
      return undefined;
    }

    let active = true;
    setMessages([]);
    if (onHighlightRef.current) {
      onHighlightRef.current([]);
    }

    const loadHistory = async () => {
      try {
        // Backend returns history in chronological order (oldest first)
        const history = await fetchMeetingChatHistory(sessionId);
        if (!active || !Array.isArray(history)) return;

        const formatted = history.map((msg, index) => ({
          id: msg.id ? `history-${msg.id}` : `history-${index}`,
          role: msg.role,
          text: msg.content,
          relevant_segment_ids: normalizeSegmentIds(msg.relevant_segment_ids),
        }));

        if (!active) return;

        // Calculate highlights based on the last chronological message
        const targetSegments = getLastAssistantSegmentsFromChronological(formatted);
        if (onHighlightRef.current) {
          onHighlightRef.current(targetSegments);
        }

        // Store messages in reverse chronological order (newest first)
        setMessages(formatted.reverse());
        messageIdRef.current = formatted.length;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Unable to load meeting chat history', error);
      }
    };

    loadHistory();
    return () => {
      active = false;
    };
  }, [sessionId]);

  const scrollToSegment = (segmentId) => {
    if (typeof document === 'undefined') {
      return;
    }
    const target = document.getElementById(`transcript-segment-${segmentId}`);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!question.trim()) {
      toast.error('Please enter a question about the meeting.');
      return;
    }
    const trimmed = question.trim();
    
    // Prepend user message
    setMessages((prev) => [
      {
        id: `user-${messageIdRef.current + 1}`,
        role: 'user',
        text: trimmed,
      },
      ...prev,
    ]);
    
    messageIdRef.current += 1;
    setQuestion(''); // Clear input immediately
    setLoading(true);
    
    try {
      const response = await chatAboutMeeting(sessionId, trimmed);
      const ids = (Array.isArray(response.relevant_segment_ids) ? response.relevant_segment_ids : [])
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value));
      
      // Prepend assistant response
      setMessages((prev) => [
        {
          id: `assistant-${messageIdRef.current + 1}`,
          role: 'assistant',
          text: response.answer || '',
          relevant_segment_ids: ids,
        },
        ...prev,
      ]);
      
      messageIdRef.current += 1;
      if (onHighlight) {
        onHighlight(ids);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Meeting chat failed', error);
      toast.error('Unable to get an answer right now.');
    } finally {
      setLoading(false);
    }
  };

  const renderMessage = (message) => {
    const isAssistant = message.role === 'assistant';
    const highlightIds = Array.isArray(message.relevant_segment_ids)
      ? message.relevant_segment_ids
      : [];
    const renderContent = () => {
      if (isAssistant) {
        const html = markdownToHtml(message.text || '');
        if (!html) {
          return <p className="mt-1 text-sm text-gray-500">No answer returned.</p>;
        }
        return (
          <div
            className="mt-1 text-sm leading-relaxed text-gray-800"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        );
      }
      return <p className="mt-1 text-sm text-gray-800 whitespace-pre-line">{message.text}</p>;
    };
    return (
      <div
        key={message.id}
        className={`rounded-md border px-3 py-2 ${isAssistant ? 'border-indigo-100 bg-indigo-50' : 'border-gray-100 bg-gray-50'
          }`}
      >
        <div className="flex items-center justify-between">
          <span className="text-xs uppercase tracking-wide text-gray-500">
            {isAssistant ? 'Assistant' : 'You'}
          </span>
          {isAssistant && highlightIds.length > 0 && (
            <button
              type="button"
              className="text-xs font-semibold text-indigo-600 hover:underline"
              onClick={() => onHighlight?.(highlightIds)}
            >
              Show segments
            </button>
          )}
        </div>
        {renderContent()}
        {isAssistant && highlightIds.length > 0 && (
          <p className="mt-1 text-xs text-gray-500">
            Highlighted transcript segments:{' '}
            {highlightIds.map((segmentId, index) => (
              <React.Fragment key={`segment-${segmentId}-${index}`}>
                {index > 0 && ', '}
                <a
                  href={`#transcript-segment-${segmentId}`}
                  className="font-semibold text-indigo-600 hover:underline"
                  onClick={(event) => {
                    event.preventDefault();
                    if (onHighlight) {
                      onHighlight(highlightIds);
                    }
                    // Delay scrolling slightly to override the TranscriptViewer's automatic scroll-to-first-highlight behavior
                    setTimeout(() => {
                      scrollToSegment(segmentId);
                    }, 150);
                  }}
                >
                  {segmentId}
                </a>
              </React.Fragment>
            ))}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="rounded-lg border bg-white p-4 shadow-sm h-full flex flex-col">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Ask the transcript</h2>
      </div>
      <form onSubmit={handleSubmit} className="space-y-3">
        <textarea
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm leading-relaxed text-gray-800 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
          rows={3}
          placeholder="What would you like to know about this meeting?"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          disabled={loading || disabled}
        />
        <button
          type="submit"
          className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300 w-full sm:w-auto"
          disabled={loading || disabled}
        >
          {loading ? 'Asking…' : 'Ask'}
        </button>
      </form>

      <div className="mt-4 flex-1 overflow-y-auto space-y-3 pr-1">
        {messages.length ? (
          messages.map((message) => renderMessage(message))
        ) : (
          <p className="text-sm text-gray-500">
            Ask a question to see the answer and highlighted transcript segments.
          </p>
        )}
      </div>
    </div>
  );
};

export default ChatPanel;
